import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import ajvErrors from 'ajv-errors';
import addDateKeyword from './dateKeyword';
import addFunctionKeyword from './functionKeyword';
import fakeData from './fakeData';

const convertType = (type) => {
  switch (type) {
    case 'jsDate':
      return { instanceof: 'Date' };
    case 'function':
      return { isFunction: 'true' };
    case 'date':
      return { type: 'string', format: 'date' };
    default:
      return { type };
  }
};

export const required = (type) => convertType(type);
export const optional = (type) => {
  if (type === 'jsDate') return { nullableinstanceof: 'Date' };
  if (type === 'function') return { isNullableFunction: 'true' };
  return { ...convertType(type), nullable: true };
};
export const array = (type) => ({ __array__: type });
export const obj = (type) => ({ __obj__: type });

const ajvInstance = new Ajv({ allowUnionTypes: true, allErrors: true });
addFormats(ajvInstance);
addFunctionKeyword(ajvInstance);
addDateKeyword(ajvInstance);
ajvErrors(ajvInstance);

class SchemaClass {
  constructor(definition, type = 'object', opts = {}) {
    if (Array.isArray(definition)) this.isSpecial = true;
    this.definition = definition;
    this.type = type;
    this.defaultValue = opts.defaultValue;
    this.compile();
  }

  buildObjectSchema(definition) {
    const properties = {};
    const keys = Object.keys(definition);
    keys.forEach((key) => properties[key] = this.buildSchema(definition[key]));
    return {
      type: 'object',
      properties,
      required: keys,
      additionalProperties: false
    };
  }

  buildArraySchema(definition) {
    const internalSchema = this.buildSchema(definition);
    return {
      type: 'array',
      items: internalSchema
    };
  }

  buildSchema(definition) {
    if (definition.__array__) return this.buildArraySchema(definition.__array__);
    if (definition.__obj__) return this.buildObjectSchema(definition.__obj__);
    if (definition.type || definition.instanceof || definition.nullableinstanceof) return definition;
    if (definition.isFunction || definition.isNullableFunction) return definition;
    return convertType(definition);
  }

  sampleData() {
    const data = fakeData(this.definition);
    return this.type === 'array' ? [data] : data;
  }

  compile() {
    switch (this.type) {
      case 'object':
        this.schema = this.buildObjectSchema(this.definition);
        break;
      case 'primitive':
        this.schema = convertType(this.definition);
        break;
      case 'primitive-nullable':
        this.schema = convertType(this.definition);
        if (this.schema.type) this.schema.nullable = true;
        if (this.schema.instanceof) this.schema = { nullableinstanceof: this.schema.instanceof };
        break;
      case 'array':
        if (!Array.isArray(this.definition) && typeof this.definition === 'object' && !this.definition.__array__) {
          this.schema = this.buildArraySchema({ __obj__: this.definition });
        } else {
          this.schema = this.buildArraySchema(this.definition);
        }
        break;
      default:
        throw `Unrecognized schema type: ${this.type}`;
    }
    this.validate = ajvInstance.compile(this.schema);
  }
}

export const isSchema = (thing) => thing instanceof SchemaClass;

const Schema = (definition, type, opts) => new SchemaClass(definition, type, opts);

export default Schema;
